import React from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import axios from "axios"
import CommunityGuidelinesAgreement from "../CommunityGuidelinesAgreement"

interface Round {
  id: number
  date: string
}

interface Community {
  id: number
  name: string
  rounds: Round[]
}

interface ProfilePhoto {
  assetUrl: string
  assetSrcset: string
  s3Url: string
}

interface AssetMap {
  phoneInHand: string
  close: string
}

interface NewFeedPostScreenProps {
  title: string
  canGoBack: boolean
  communities: Array<Community>
  selectedCommunityId: number
  roundId: number | null
  skipPath: string | null
  communityGuidelinesAgreementRequired: boolean
  canMakeGlobalPosts: boolean
  communityLabel?: string
  postPath?: string
  postType: "profile_photo" | null
  profilePhoto: ProfilePhoto | null
  assets: AssetMap
}

export default function NewFeedPostScreen({
  title,
  canGoBack,
  communities,
  selectedCommunityId,
  roundId,
  skipPath,
  communityGuidelinesAgreementRequired,
  canMakeGlobalPosts,
  communityLabel,
  postPath,
  postType,
  profilePhoto,
  assets
}: NewFeedPostScreenProps) {
  const [files, setFiles] = React.useState<File[]>([])
  const [s3Urls, setS3Urls] = React.useState<string[]>([])
  const [fileProgresses, setFileProgresses] = React.useState<number[]>([])
  const [uploadsComplete, setUploadsComplete] = React.useState(false)

  async function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    setUploadsComplete(false)
    setFileProgresses([])
    setS3Urls([])
    setFiles(Array.from(e.target.files).slice(0, 10))
  }

  // This will get the S3 URLs for the files
  React.useEffect(() => {
    setUploadsComplete(false)

    const fetchS3Urls = async () => {
      if (files.length > 0) {
        try {
          const filename = files.map((f) => f.name)
          const response = await axios.post("/api/feed_posts/presigned_s3_url", { filename })
          if (response.data?.urls) {
            const newS3Urls = []
            for (const url of response.data.urls) {
              newS3Urls.push(url)
            }
            setS3Urls(newS3Urls)
          } else {
            setS3Urls([])
          }
        } catch (e) {
          console.error("Could not get S3 URL for files", e)
          setS3Urls([])
        }
      } else {
        setS3Urls([])
      }
    }

    fetchS3Urls()
  }, [files])

  // Once we have S3 URLs, this will upload the files
  React.useEffect(() => {
    if (s3Urls.length === 0) {
      return
    }

    setUploadsComplete(false)
    setFileProgresses(Array(s3Urls.length).fill(0))

    const uploads = s3Urls.map((s3Url, index) => {
      const file = files[index]
      return axios.put(s3Url, file, {
        headers: { "Content-Type": file.type },
        timeout: 120000,
        onUploadProgress: (e) => {
          if (!e.total) {
            return
          }
          setFileProgresses((prevFileProgresses) => {
            const newFileProgresses = [...prevFileProgresses]
            newFileProgresses[index] = e.loaded / e.total
            return newFileProgresses
          })
        }
      })
    })

    const uploadFiles = async () => {
      try {
        await Promise.all(uploads)
        setUploadsComplete(true)
      } catch (e) {
        console.error("Could not upload files", e)
        setUploadsComplete(false)
      }
    }

    uploadFiles()
  }, [s3Urls])

  function goBackOrHome() {
    if (canGoBack) {
      window.history.back()
    } else {
      // @ts-ignore
      window.location = "/app"
    }
  }

  if (!canMakeGlobalPosts && (!communities || communities.length === 0)) {
    return (
      <div className="app-screen-container new-feed-post recent-round-required" data-turbo="false">
        <div className="screen-heading">
          <h1>Join A League</h1>
          <p>You must join at least one league before you can make a post.</p>
          <div className="screen-body">
            <button className="action-button block" onClick={goBackOrHome}>
              Continue
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="app-screen-container new-feed-post" data-turbo="false">
      <Content
        title={title}
        handleFilesSelected={handleFilesSelected}
        files={files}
        s3Urls={s3Urls}
        fileProgresses={fileProgresses}
        uploadsComplete={uploadsComplete}
        communities={communities}
        selectedCommunityId={selectedCommunityId}
        roundId={roundId}
        skipPath={skipPath}
        communityGuidelinesAgreementRequired={communityGuidelinesAgreementRequired}
        canMakeGlobalPosts={canMakeGlobalPosts}
        communityLabel={communityLabel}
        postPath={postPath}
        postType={postType}
        profilePhoto={profilePhoto}
        assets={assets}
      />
    </div>
  )
}

interface ContentProps {
  title: string
  handleFilesSelected: (e: React.ChangeEvent<HTMLInputElement>) => void
  files: File[] | null
  s3Urls: string[] | null
  fileProgresses: number[] | null
  uploadsComplete: boolean
  communities: Array<Community>
  selectedCommunityId: number
  roundId: number | null
  skipPath: string | null
  communityGuidelinesAgreementRequired: boolean
  canMakeGlobalPosts: boolean
  communityLabel?: string
  postPath?: string
  postType: "profile_photo" | null
  profilePhoto: ProfilePhoto | null
  assets: AssetMap
}

function Content({
  title,
  handleFilesSelected,
  files,
  s3Urls,
  fileProgresses,
  uploadsComplete,
  communities,
  selectedCommunityId,
  roundId,
  skipPath,
  communityGuidelinesAgreementRequired,
  canMakeGlobalPosts,
  communityLabel,
  postPath,
  postType,
  profilePhoto,
  assets
}: ContentProps) {
  if (postType == "profile_photo" || (files && files.length > 0)) {
    let totalProgress = 0
    let filesUploaded = 0
    for (let i = 0; i < files.length; i++) {
      totalProgress += fileProgresses[i] ?? 0
      if ((fileProgresses[i] ?? 0) >= 1) filesUploaded++
    }

    if (postType == "profile_photo" || uploadsComplete) {
      return (
        <PostPreview
          title={title}
          files={files}
          s3Urls={s3Urls}
          communities={communities}
          selectedCommunityId={selectedCommunityId}
          roundId={roundId}
          communityGuidelinesAgreementRequired={communityGuidelinesAgreementRequired}
          canMakeGlobalPosts={canMakeGlobalPosts}
          communityLabel={communityLabel}
          postPath={postPath}
          postType={postType}
          profilePhoto={profilePhoto}
          assets={assets}
        />
      )
    } else {
      return <FilesUploading filesUploaded={filesUploaded} totalProgress={totalProgress} totalFiles={files.length} />
    }
  }

  return <SelectPhotos title={title} handleFilesSelected={handleFilesSelected} roundId={roundId} skipPath={skipPath} />
}

interface SelectPhotosProps {
  title: string
  handleFilesSelected: (e: React.ChangeEvent<HTMLInputElement>) => void
  roundId: number | null
  skipPath: string | null
}

function SelectPhotos({ title, handleFilesSelected, roundId, skipPath }: SelectPhotosProps) {
  return (
    <>
      <div className="screen-heading">
        <h1>{roundId ? "Share Photos" : title}</h1>
        {roundId ? (
          <p>
            Did you take some photos while you were playing that you want to share? Upload up to 10 to create a post
            that others can like and comment on.
          </p>
        ) : (
          <p>Select up to 10 photos to include in your post</p>
        )}
      </div>

      <div className="screen-body">
        <div className="add-photo">
          <label htmlFor="fileinput">
            <div className="action-button">Add Photos</div>
          </label>
        </div>
        <input id="fileinput" type="file" multiple accept="image/*" onChange={handleFilesSelected} />

        {roundId && skipPath && (
          <div className="secondary-links">
            <a href={skipPath}>Maybe later</a>
          </div>
        )}
      </div>
    </>
  )
}

interface FilesUploadingProps {
  filesUploaded: number
  totalProgress: number
  totalFiles: number
}

function FilesUploading({ filesUploaded, totalProgress, totalFiles }: FilesUploadingProps) {
  // Progress hits 100% when bytes leave the browser; we still wait on S3's response.
  const processing = totalFiles > 0 && filesUploaded === totalFiles
  const percentComplete = Math.round((totalProgress / totalFiles) * 100)

  return (
    <>
      <div className="screen-heading">
        <h1>{processing ? "Processing..." : "Uploading..."}</h1>
        <p>
          {processing
            ? "Finishing upload — this can take a moment on a slow connection."
            : `${filesUploaded} of ${totalFiles} photos uploaded`}
        </p>
      </div>

      <div className="screen-body">
        <div className={`files-uploading ${processing ? "processing" : ""}`}>
          {processing ? (
            <div className="progress-track" aria-hidden="true">
              <div className="progress-bar indeterminate" />
            </div>
          ) : (
            <progress value={totalProgress} max={totalFiles} />
          )}
          <div className="percent-complete">{processing ? "Processing..." : `${percentComplete}% complete`}</div>
        </div>
      </div>
    </>
  )
}

interface PostPreviewProps {
  title: string
  files: File[]
  s3Urls: string[]
  communities: Array<Community>
  selectedCommunityId: number
  roundId: number | null
  communityGuidelinesAgreementRequired: boolean
  canMakeGlobalPosts: boolean
  communityLabel?: string
  postPath?: string
  postType: "profile_photo" | null
  profilePhoto: ProfilePhoto | null
  assets: AssetMap
}

function PostPreview({
  title,
  files,
  s3Urls,
  communities,
  selectedCommunityId,
  roundId,
  communityGuidelinesAgreementRequired,
  canMakeGlobalPosts,
  communityLabel,
  postPath,
  postType,
  profilePhoto,
  assets
}: PostPreviewProps) {
  const [message, setMessage] = React.useState("")
  const [community, setCommunity] = React.useState<Community | null>(
    selectedCommunityId ? communities?.find((g) => g.id === selectedCommunityId) : communities?.at(0)
  )
  const [round, setRound] = React.useState<Round | null>(null)
  const [disabled, setDisabled] = React.useState(false)
  const [error, setError] = React.useState("")
  const [showCommunityGuidelines, setShowCommunityGuidelines] = React.useState(false)

  function selectCommunity(communityId: number) {
    const community = communities?.find((c) => c.id === communityId)
    setCommunity(community || null)
  }

  function selectRound(roundId: number | null) {
    const round = community?.rounds.find((r) => r.id === roundId)
    setRound(round || null)
  }

  async function submitPost() {
    const attachments =
      postType === "profile_photo"
        ? [
            {
              s3Url: profilePhoto.s3Url,
              mediaType: "image"
            }
          ]
        : files.map((file, index) => {
            return {
              s3Url: baseS3Url(s3Urls[index]),
              fileName: file.name,
              fileSize: file.size,
              mimeType: file.type,
              mediaType: "image"
            }
          })

    const body = {
      message,
      communityId: community?.id,
      roundId: roundId || round?.id,
      next: roundId ? "round" : "post",
      attachments
    }

    try {
      const response = await axios.post(postPath || "/app/p/new", body)
      if (response.status === 200 && response.data.url) {
        window.location = response.data.url
      } else if (response.status === 422 && response.data.error) {
        setError(response.data.error)
        setShowCommunityGuidelines(false)
        setDisabled(false)
      } else {
        setError("Could not save post")
        setShowCommunityGuidelines(false)
        setDisabled(false)
      }
    } catch (e) {
      console.error("Could not save post", e)
      setError("Could not save post")
      setShowCommunityGuidelines(false)
      setDisabled(false)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setDisabled(true)

    if (communityGuidelinesAgreementRequired) {
      window.scrollTo(0, 0)
      setShowCommunityGuidelines(true)
    } else {
      await submitPost()
    }
  }

  if (showCommunityGuidelines) {
    return <CommunityGuidelinesAgreement assets={assets} onAgree={submitPost} />
  }

  return (
    <form method="post" onSubmit={handleSubmit}>
      <div className="screen-heading">
        <h1>{title}</h1>
        <p>
          {postType == "profile_photo"
            ? "Your profile photo has been updated. You can post it to the league feed with a caption."
            : "Add a caption to your post"}
        </p>
      </div>

      <div className="screen-body">
        <div className="post-preview">
          {postType == "profile_photo" && profilePhoto ? (
            <img
              src={profilePhoto.assetUrl}
              srcSet={profilePhoto.assetSrcset}
              alt="Profile Photo"
              style={{ marginBottom: 16 }}
            />
          ) : s3Urls.length === 1 ? (
            <ImageWithPlaceholder src={assetUrl(s3Urls[0])} alt="Photo" style={{ marginBottom: 16 }} />
          ) : (
            <Swiper loop pagination>
              {s3Urls.map((s3Url, index) => (
                <SwiperSlide key={index}>
                  <ImageWithPlaceholder src={assetUrl(s3Url)} alt={`Photo ${index + 1}`} />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
          {error && <div id="error">{error}</div>}
          <textarea
            name="message"
            placeholder="Write a caption..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />

          {!roundId &&
            communities &&
            communities.length > 0 &&
            (communityLabel || communities.length > (canMakeGlobalPosts ? 0 : 1)) && (
            <label>
              {communityLabel || "League"}
              <select
                name="communty"
                value={community ? community.id : ""}
                onChange={(e) => selectCommunity(parseInt(e.target.value) || null)}
              >
                {canMakeGlobalPosts && !communityLabel && <option value="">No specific league</option>}
                {communities.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {!roundId && community && community.rounds && community.rounds.length > 0 && (
            <label>
              Round
              <select
                name="round"
                value={round ? round.id : ""}
                onChange={(e) => selectRound(parseInt(e.target.value) || null)}
              >
                <option value="">No specific round</option>
                {community.rounds.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.date}
                  </option>
                ))}
              </select>
            </label>
          )}

          <button type="submit" className="action-button block" disabled={disabled}>
            Save Post
          </button>
        </div>
      </div>
    </form>
  )
}

// This will both show a placeholder image and handle the image not loading.
// In testing, I noticed that there seems to be a race condition where
// Imgix tries to get the image from S3 before it's fully uploaded.
// Imgix has then cached the 404 response and the image never loads.
// I changed the Imgix error cache time to 0,
// which allows us to retry the image load after a delay.
function ImageWithPlaceholder({ src, alt, style }: { src: string; alt: string; style?: React.CSSProperties }) {
  const [retries, setRetries] = React.useState(0)
  const [loaded, setLoaded] = React.useState(false)
  const size = Math.min(window.screen.availWidth, 688)

  const handleError = () => {
    if (retries < 3) {
      setTimeout(() => setRetries(retries + 1), 1000)
    }
  }

  const imgSrc = retries > 0 ? `${src}&retry=${retries}` : src

  return (
    <div className="imageHolder">
      {loaded ? (
        <></>
      ) : (
        <img
          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mM88x8AAp0BzdNtlUkAAAAASUVORK5CYII="
          style={{ width: size, height: size, marginBottom: 16 }}
          width={size}
          height={size}
          alt=""
        />
      )}
      <img src={imgSrc} alt={alt} style={style} onLoad={() => setLoaded(true)} onError={handleError} />
    </div>
  )
}

function baseS3Url(s3Url: string) {
  const url = new URL(s3Url)
  return `${url.origin}${url.pathname}`
}

function assetUrl(s3Url: string) {
  const contentWidth = Math.min(window.screen.availWidth, 688)
  const imageWidth = contentWidth * window.devicePixelRatio
  const url = new URL(s3Url)
  return `${url.origin.replace("s3.amazonaws.com", "imgix.net")}${
    url.pathname
  }?w=${imageWidth}&h=${imageWidth}&fit=crop&crop=faces`
}
